import { type ReactNode } from "react";
import { useAiData, useTitle } from "../ai/parts";
import { executionTone, money, number, operationName, when } from "../ai/ai";
import { comparison, sharedQuery, UsageFilters, type Totals } from "../ai/UsagePage";
import {
  Counter,
  DataTable,
  Indicator,
  IndicatorSkeleton,
  Indicators,
  NoResults,
  RowsSkeleton,
  Section,
  useFilters,
  useIsMobile,
} from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts } from "../texts";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState, ServiceState } from "../ui/pieces";
import { moduleName, orgStatus, plural, whenIn } from "./organizations";
import { actorText, auditText } from "./security";
import { issueTitle, moduleName as hubModuleName, statusView } from "../hub/hub";
import { SimpleTable } from "./SimpleTable";
import styles from "./orgs.module.css";
import own from "./security.module.css";

type OrgHead = { id: number; name: string; code: string; timezone: string };

type Tab = "diagnostic" | "usage" | "audit";

/** Encabezado «Uso y soporte» con su acción y las pestañas Diagnóstico · Uso de IA · Auditoría (C03). */
function SupportHead({ orgId, current, action }: { orgId: number; current: Tab; action?: ReactNode }) {
  const { L } = useTexts();
  const tabs: { key: Tab; label: string; href: string }[] = [
    { key: "diagnostic", label: L("ADM_SUP_TAB_DIAGNOSTIC"), href: `/Organizations/Soporte?id=${orgId}` },
    { key: "usage", label: L("ADM_SUP_TAB_USAGE"), href: `/Organizations/Uso?id=${orgId}` },
    { key: "audit", label: L("ADM_SUP_TAB_AUDIT"), href: `/Organizations/Auditoria?id=${orgId}` },
  ];
  return (
    <>
      <PageHead title={L("ADM_ORG_USAGE")}>{action}</PageHead>
      <nav className={`tm-tabs ${ui.tabs}`} aria-label={L("ADM_ORG_USAGE")} data-part="pestanas">
        {tabs.map((tab) => (
          <a key={tab.key} href={tab.href} aria-current={tab.key === current ? "page" : undefined}>
            {tab.label}
          </a>
        ))}
      </nav>
    </>
  );
}

/** Aviso de un bloque cuyo servicio no respondió, con «Reintentar» (nunca 0 ni vacío, H-3). */
function Unavailable({ retry }: { retry: () => void }) {
  const { L } = useTexts();
  return (
    <Notice
      tone="error"
      action={
        <button type="button" className="tm-btn secondary" onClick={retry}>
          {L("ADM_RETRY")}
        </button>
      }
    >
      {L("ADM_UNAVAILABLE_LINE")}
    </Notice>
  );
}

type ProblemChannel = {
  channelId: number;
  name: string;
  channelType: string;
  phone: string | null;
  isSimulation: boolean;
  active: boolean;
  defaultInbox: string | null;
};

type HealthAlert = { level: string; name: string | null; provider: string | null; failures: number; href: string };

type Support = {
  organization: OrgHead;
  status: string | null;
  people: { total: number; blocked: number } | null;
  channels: { total: number; active: number; simulation: boolean; problems: ProblemChannel[] } | null;
  alerts: HealthAlert[] | null;
};

/** R7 · B9 (AR18): problemas del Error Hub de la empresa; null sin permiso, `available: false` si no respondió. */
type HubBlock = {
  hub: {
    available: boolean;
    total?: number;
    items?: {
      id: string;
      title: string | null;
      errorKey: string | null;
      module: string | null;
      status: string | null;
      occurrences: number;
      lastSeen: string | null;
    }[];
  } | null;
};

/**
 * `emp-diagnostico` · Uso y soporte › Diagnóstico (§5.8, F4: Diagnóstico y Eventos técnicos en una): estado de la
 * empresa, personas, canales y problemas con su destino, los canales con problemas (inactivos o sin bandeja) y las
 * alertas de salud de IA, que son de toda la plataforma. «Simular» lleva al Simulador.
 */
export function DiagnosticPage({ orgId, canErrorHub = false }: { orgId: number; canErrorHub?: boolean }) {
  const { L, locale } = useTexts();
  const data = useAiData<Support>(`/api/admin/organizations/${orgId}/support`);
  // El bloque del Error Hub es otra lectura, a la vez: Diagnóstico no espera al Error Hub (rendimiento sin regresión).
  const hubData = useAiData<HubBlock>(canErrorHub ? `/api/admin/organizations/${orgId}/support/hub` : null);
  const hubBlock = hubData.data?.hub ?? (hubData.state === "failed" ? { available: false } : null);
  useOpenOrg(data.data?.organization ?? null);
  useTitle(`${L("ADM_ORG_USAGE")} · ${L("ADM_SUP_TAB_DIAGNOSTIC")}`);
  if (data.state === "missing") return <OrgNotFound />;
  const simulate = (
    <a className="tm-btn secondary" href={`/Organizations/Simulator?id=${orgId}`}>
      {L("ADM_SUP_SIMULATE")}
    </a>
  );
  const head = <SupportHead orgId={orgId} current="diagnostic" action={simulate} />;
  const support = data.data;
  if (!support && data.state === "failed")
    return (
      <PageState
        head={head}
        title={data.error?.status === 403 ? L("ADM_FORBIDDEN") : L("ADM_UNAVAILABLE")}
        line={data.error?.status === 403 ? L("ADM_FORBIDDEN_LINE") : L("ADM_UNAVAILABLE_LINE")}
        action={
          data.error?.status === 403 ? undefined : (
            <button type="button" className="tm-btn secondary" onClick={data.retry}>
              {L("ADM_RETRY")}
            </button>
          )
        }
      />
    );
  if (!support)
    return (
      <>
        {head}
        <IndicatorSkeleton count={4} label={L("ADM_SUP_TAB_DIAGNOSTIC")} />
        <RowsSkeleton rows={2} table />
      </>
    );

  const status = orgStatus(L, support.status ?? "");
  const channels = support.channels;
  const problems = channels?.problems ?? [];
  const channelsHref = `/Organizations/Canales?id=${orgId}`;
  const problemLine = (channel: ProblemChannel) =>
    !channel.active ? L("ADM_SUP_CH_INACTIVE_LINE") : L("ADM_SUP_CH_NO_INBOX_LINE");
  const channelSub = (channel: ProblemChannel) =>
    [
      channel.channelType === "WHATSAPP" ? L("ADM_SUP_CH_WHATSAPP") : L("USAGE_UNKNOWN"),
      channel.phone,
      channel.isSimulation ? L("ADM_AI_MODE_SIMULATION") : null,
    ]
      .filter(Boolean)
      .join(" · ");
  return (
    <>
      {head}
      <Indicators label={L("ADM_SUP_TAB_DIAGNOSTIC")} count={4}>
        <Indicator
          label={L("ADM_COMPANY")}
          value={status.text}
          small
          line={support.organization.name}
          href={`/Organizations/Detail?id=${orgId}`}
        />
        <Indicator
          label={L("ADM_SUP_USERS")}
          value={support.people ? support.people.total.toLocaleString(locale) : "—"}
          muted={!support.people}
          line={
            support.people
              ? plural(L, locale, support.people.blocked, "ADM_SUP_N_BLOCKED_ONE", "ADM_SUP_N_BLOCKED")
              : L("ADM_ORGS_NOT_AVAILABLE")
          }
          href={
            support.people && support.people.blocked > 0
              ? `/Organizations/Usuarios?id=${orgId}&status=blocked`
              : `/Organizations/Usuarios?id=${orgId}`
          }
        />
        <Indicator
          label={L("ADM_SUP_ACTIVE_CHANNELS")}
          value={
            channels
              ? formatText(
                  L("ADM_SUP_X_OF_Y"),
                  channels.active.toLocaleString(locale),
                  channels.total.toLocaleString(locale),
                )
              : "—"
          }
          muted={!channels}
          line={
            !channels
              ? L("ADM_ORGS_NOT_AVAILABLE")
              : channels.simulation
                ? L("ADM_SUP_PLUS_SIMULATION")
                : L("ADM_SUP_CHANNELS_LINE")
          }
          href={channelsHref}
        />
        <Indicator
          label={L("ADM_SUP_PROBLEMS")}
          value={channels ? problems.length.toLocaleString(locale) : "—"}
          muted={!channels}
          line={
            !channels
              ? L("ADM_ORGS_NOT_AVAILABLE")
              : problems.length === 0
                ? L("ADM_SUP_NO_PROBLEMS_SHORT")
                : problems.length === 1
                  ? problemLine(problems[0]!)
                  : plural(L, locale, problems.length, "ADM_SUP_N_CHANNELS_ONE", "ADM_SUP_N_CHANNELS")
          }
          href={channelsHref}
        />
      </Indicators>
      <Section
        id="sup-channels"
        title={L("ADM_SUP_CHANNELS_TITLE")}
        action={
          <a className="tm-btn secondary" href={channelsHref}>
            {L("ADM_SUP_GO_CHANNELS")}
          </a>
        }
      >
        {!channels ? (
          <Unavailable retry={data.retry} />
        ) : problems.length === 0 ? (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_SUP_NO_PROBLEMS")}</strong>
            <span>{L("ADM_SUP_NO_PROBLEMS_LINE")}</span>
          </div>
        ) : (
          <SimpleTable
            label={L("ADM_SUP_CHANNELS_TITLE")}
            rows={problems}
            rowKey={(channel) => channel.channelId}
            columns={[
              {
                key: "channel",
                title: L("ADM_SUP_COL_CHANNEL"),
                render: (channel) => (
                  <span className={own.channel}>
                    <span className={own.channelMark} aria-hidden="true">
                      <ChatIcon name={channel.channelType === "WHATSAPP" ? "whatsapp" : "message-circle"} />
                    </span>
                    <span className={styles.personText}>
                      <strong>{channel.name}</strong>
                      <span>{channelSub(channel)}</span>
                    </span>
                  </span>
                ),
              },
              {
                key: "status",
                title: L("ADM_PPL_COL_STATUS"),
                render: (channel) => L(channel.active ? "ADM_SUP_CH_ACTIVE" : "ADM_SUP_CH_INACTIVE"),
              },
              {
                key: "inbox",
                title: L("ADM_SUP_COL_DEFAULT_INBOX"),
                render: (channel) => channel.defaultInbox ?? L("ADM_PPL_NO_INBOX"),
              },
            ]}
            mobile={(channel) => ({
              title: channel.name,
              line: [
                channelSub(channel),
                L(channel.active ? "ADM_SUP_CH_ACTIVE" : "ADM_SUP_CH_INACTIVE"),
                channel.defaultInbox ?? L("ADM_PPL_NO_INBOX"),
              ].join(" · "),
            })}
          />
        )}
      </Section>
      {canErrorHub && (hubBlock || !hubData.data) ? (
        // R7 · B9 (AR18): problemas del Error Hub de esta empresa, con su botón a Problemas filtrado (solo con permiso).
        <Section
          id="sup-hub"
          title={L("ADM_SUP_HUB_TITLE")}
          line={L("ADM_SUP_HUB_LINE")}
          action={
            <a className="tm-btn secondary" href={`/ErrorHub/Issues?status=attention&organizationId=${orgId}`}>
              {L("ADM_SUP_HUB_SEE")}
            </a>
          }
        >
          {!hubBlock ? (
            <RowsSkeleton rows={2} />
          ) : !hubBlock.available ? (
            <Unavailable retry={hubData.retry} />
          ) : !hubBlock.items?.length ? (
            <div className="tm-empty" role="status" data-part="vacio">
              <strong>{L("ADM_SUP_HUB_NONE")}</strong>
            </div>
          ) : (
            <ul className={`tm-list ${ui.list}`} aria-label={L("ADM_SUP_HUB_TITLE")} data-part="lista">
              {hubBlock.items.map((issue) => (
                <li key={issue.id} className={ui.row}>
                  <a
                    className={ui.rowLinkArea}
                    href={`/ErrorHub/Issues?status=attention&organizationId=${orgId}&issue=${issue.id}`}
                  >
                    <span className={own.alertMark} aria-hidden="true">
                      <ChatIcon name="circle-alert" />
                    </span>
                    <span className={ui.rowText}>
                      <span className={ui.line1}>
                        <strong>{issueTitle(L, issue)}</strong>
                      </span>
                      <span className={ui.line2}>
                        {[
                          hubModuleName(L, issue.module),
                          statusView(L, issue.status).text,
                          plural(L, locale, issue.occurrences, "ADM_HUB_N_OCCURRENCES_ONE", "ADM_HUB_N_OCCURRENCES"),
                          when(L, locale, issue.lastSeen),
                        ].join(" · ")}
                      </span>
                    </span>
                    <span className={ui.rowEnd} aria-hidden="true">
                      <ChatIcon name="chevron-right" />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </Section>
      ) : null}
      <Section id="sup-alerts" title={L("ADM_SUP_ALERTS_TITLE")} line={L("ADM_SUP_ALERTS_LINE")}>
        {!support.alerts ? (
          <Unavailable retry={data.retry} />
        ) : support.alerts.length === 0 ? (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_SUP_NO_ALERTS")}</strong>
            <a className="tm-btn secondary" href="/Ai/Config/Index">
              {L("ADM_SUP_SEE_AI")}
            </a>
          </div>
        ) : (
          <ul className={`tm-list ${ui.list}`} aria-label={L("ADM_SUP_ALERTS_TITLE")} data-part="lista">
            {support.alerts.map((alert) => {
              const connection = alert.level === "CONNECTION";
              const title = connection
                ? alert.name && alert.provider
                  ? formatText(L("ADM_SUP_ALERT_KEY"), alert.name, alert.provider)
                  : L("ADM_SUP_ALERT_KEY_UNKNOWN")
                : (alert.name ?? L("USAGE_UNKNOWN"));
              return (
                <li key={`${alert.level}-${alert.href}-${title}`} className={ui.row}>
                  <span className={`${own.alertMark}${connection ? ` ${own.alertFail}` : ""}`} aria-hidden="true">
                    <ChatIcon name="circle-alert" />
                  </span>
                  <span className={ui.rowText}>
                    <span className={ui.line1}>
                      <strong>{title}</strong>
                    </span>
                    <span className={ui.line2}>
                      {`${L(connection ? "ADM_SUP_LEVEL_CONNECTION" : "ADM_SUP_LEVEL_MODEL")} · ${plural(
                        L,
                        locale,
                        alert.failures,
                        "ADM_SUP_N_FAILURES_ONE",
                        "ADM_SUP_N_FAILURES",
                      )}`}
                    </span>
                  </span>
                  <span className={ui.rowEnd}>
                    <a className="tm-btn secondary" href={alert.href}>
                      {L(connection ? "ADM_SUP_REVIEW" : "ADM_SUP_SEE_MODEL")}
                    </a>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
    </>
  );
}

type ExecutionRow = {
  executionId: number;
  operationType: string;
  status: string;
  totalInputTokens: number | null;
  totalOutputTokens: number | null;
  totalCost: number | null;
  currency: string | null;
  startedAt: string;
};

type Summary = { totals: Totals; previous: Totals };

/**
 * `emp-uso` · Uso y soporte › Uso de IA (§5.8, D14): los totales del período los suma el servidor (B1, los mismos de IA ›
 * Uso y costos) y la tabla pagina en AI Management (B2); sin aviso de «últimas 200». Período y Reales · Simulación en la
 * dirección. Clic en una ejecución abre IA › Uso y costos › Ejecución.
 */
export function OrgUsagePage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const { params, get, set } = useFilters();
  const head = useAiData<{ organization: OrgHead }>(`/api/admin/organizations/${orgId}/head`);
  useOpenOrg(head.data?.organization ?? null);
  useTitle(`${L("ADM_ORG_USAGE")} · ${L("ADM_SUP_TAB_USAGE")}`);
  const shared = new URLSearchParams(sharedQuery(params));
  shared.set("organizationId", String(orgId));
  const query = shared.toString();
  const summary = useAiData<Summary>(`/api/admin/ai/usage/summary?${query}&pageSize=1`);
  const list = useServerList<ExecutionRow>(`/api/admin/ai/usage/executions?${query}`);
  if (head.state === "missing") return <OrgNotFound />;
  const top = <SupportHead orgId={orgId} current="usage" />;
  if (list.state === "forbidden" || summary.error?.status === 403)
    return <PageState head={top} title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;
  const totals = summary.data?.totals ?? null;
  const previous = summary.data?.previous ?? null;
  const percent = (part: number, whole: number) =>
    whole === 0 ? "0" : ((part / whole) * 100).toLocaleString(locale, { maximumFractionDigits: 1 });
  const tokens = (row: ExecutionRow) =>
    row.totalInputTokens == null && row.totalOutputTokens == null
      ? "—"
      : `${number(locale, row.totalInputTokens)} / ${number(locale, row.totalOutputTokens)}`;
  const filtered = !!get("type");
  return (
    <>
      {top}
      <div className={ui.toolbar}>
        <UsageFilters withOrganization={false} />
      </div>
      {summary.state === "failed" ? (
        <Unavailable retry={summary.retry} />
      ) : totals && previous ? (
        <Indicators label={L("ADM_SUP_TAB_USAGE")} count={5}>
          <Indicator
            label={L("ADM_AI_EXECUTIONS")}
            value={totals.executions.toLocaleString(locale)}
            line={comparison(L, locale, totals.executions, previous.executions)}
          />
          <Indicator
            label={L("ADM_AI_WITH_ERROR")}
            value={totals.errors.toLocaleString(locale)}
            line={
              totals.executions === 0
                ? L("ADM_AI_NO_EXECUTIONS_PERIOD")
                : formatText(L("ADM_AI_ERROR_PCT"), percent(totals.errors, totals.executions))
            }
          />
          <Indicator
            label={L("ADM_AI_INPUT_TOKENS")}
            value={totals.inputTokens.toLocaleString(locale)}
            small
            line={L("ADM_SUP_INPUT_TOKENS_LINE")}
          />
          <Indicator
            label={L("ADM_AI_OUTPUT_TOKENS")}
            value={totals.outputTokens.toLocaleString(locale)}
            small
            line={L("ADM_SUP_OUTPUT_TOKENS_LINE")}
          />
          <Indicator
            label={L("ADM_AI_COST")}
            value={money(locale, totals.cost, totals.currency)}
            small
            muted={totals.cost == null}
            line={
              totals.mixedCurrencies
                ? L("ADM_AI_COST_MIXED")
                : totals.unpricedExecutions > 0
                  ? plural(L, locale, totals.unpricedExecutions, "ADM_AI_UNPRICED_ONE", "ADM_AI_UNPRICED")
                  : L("ADM_AI_COST_LINE")
            }
          />
        </Indicators>
      ) : (
        <IndicatorSkeleton count={5} label={L("ADM_SUP_TAB_USAGE")} />
      )}
      {list.state === "loading" ? (
        <RowsSkeleton rows={6} table />
      ) : list.state === "error" || list.state === "notFound" ? (
        <Unavailable retry={list.reload} />
      ) : list.items.length === 0 ? (
        filtered ? (
          <NoResults query="" things="" onClear={() => set({ type: "" })} />
        ) : (
          <div className="tm-empty" role="status" data-part="vacio">
            <strong>{L("ADM_AI_NO_USAGE")}</strong>
            <span>{L("ADM_AI_NO_USAGE_LINE")}</span>
            <button type="button" className="tm-btn secondary" onClick={() => set({ period: "90d" })}>
              {L("ADM_AI_SEE_90D")}
            </button>
          </div>
        )
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, "ADM_AI_N_EXECUTIONS_ONE", "ADM_AI_N_EXECUTIONS")}</Counter>
          <DataTable<ExecutionRow>
            label={L("ADM_AI_EXECUTIONS")}
            rows={list.items}
            rowKey={(row) => row.executionId}
            href={(row) => `/Ai/Uso/Detail/${row.executionId}`}
            columns={[
              {
                key: "execution",
                title: L("ADM_AI_COL_EXECUTION"),
                render: (row) => (
                  <span className={ui.cellMain}>
                    <strong>#{row.executionId}</strong>
                    <span className={ui.cellSub}>{operationName(L, row.operationType)}</span>
                  </span>
                ),
              },
              {
                key: "status",
                title: L("ADM_AI_COL_STATUS"),
                render: (row) => {
                  const tone = executionTone(L, row.status);
                  return <ServiceState tone={tone.tone}>{tone.text}</ServiceState>;
                },
              },
              { key: "start", title: L("ADM_AI_COL_START"), render: (row) => when(L, locale, row.startedAt) },
              { key: "tokens", title: L("ADM_AI_COL_TOKENS_IO"), numeric: true, render: tokens },
              {
                key: "cost",
                title: L("ADM_AI_COST"),
                numeric: true,
                render: (row) => money(locale, row.totalCost, row.currency),
              },
            ]}
            mobile={(row) => {
              const tone = executionTone(L, row.status);
              return {
                title: `#${row.executionId}`,
                tag: <ServiceState tone={tone.tone}>{tone.text}</ServiceState>,
                line: [operationName(L, row.operationType), when(L, locale, row.startedAt), tokens(row)].join(" · "),
              };
            }}
          />
          <ListFooter list={list} />
        </>
      )}
    </>
  );
}

type AuditRow = {
  eventId: number;
  occurredAt: string;
  kind: string;
  actor: string | null;
  actorContext: string;
  target: string | null;
  module: string | null;
};

/**
 * `emp-auditoria` · Uso y soporte › Auditoría (§5.8, B3): los eventos de la empresa paginados en Identity (antes, la
 * ficha de cada persona). Cuándo · Quién · Qué pasó · A quién · Resultado, con frases del negocio; D7: sin el JSON.
 */
export function AuditPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const mobile = useIsMobile();
  const list = useServerList<AuditRow>(`/api/admin/organizations/${orgId}/audit`);
  const org = (list.extra?.organization as OrgHead | undefined) ?? null;
  useOpenOrg(org);
  useTitle(`${L("ADM_ORG_USAGE")} · ${L("ADM_SUP_TAB_AUDIT")}`);
  if (list.state === "notFound") return <OrgNotFound />;
  const head = <SupportHead orgId={orgId} current="audit" />;
  if (list.state === "forbidden")
    return <PageState head={head} title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;
  const zone = org?.timezone ?? "America/Lima";
  const what = (row: AuditRow) => auditText(L, row.kind, row.module ? moduleName(L, row.module) : null);
  const who = (row: AuditRow) => actorText(L, row.actor, row.actorContext);
  const whoName = (row: AuditRow) =>
    row.actorContext === "SYSTEM" || !row.actor ? L("ADM_PPL_ACTOR_SYSTEM") : row.actor;
  return (
    <>
      {head}
      {list.state === "loading" ? (
        <RowsSkeleton rows={5} table={!mobile} />
      ) : list.state === "error" ? (
        <Unavailable retry={list.reload} />
      ) : list.items.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_SUP_NO_EVENTS")}</strong>
          <span>{L("ADM_SUP_NO_EVENTS_LINE")}</span>
          <a className="tm-btn secondary" href={`/Organizations/Usuarios?id=${orgId}`}>
            {L("ADM_SEC_SEE_PEOPLE")}
          </a>
        </div>
      ) : (
        <>
          <Counter>{plural(L, locale, list.total, "ADM_SUP_N_EVENTS_ONE", "ADM_SUP_N_EVENTS")}</Counter>
          <SimpleTable
            label={L("ADM_SUP_TAB_AUDIT")}
            rows={list.items}
            rowKey={(row) => row.eventId}
            columns={[
              { key: "when", title: L("ADM_SUP_COL_WHEN"), render: (row) => whenIn(L, locale, row.occurredAt, zone) },
              { key: "who", title: L("ADM_PPL_COL_WHO"), render: who },
              { key: "what", title: L("ADM_PPL_COL_WHAT"), render: what },
              { key: "target", title: L("ADM_SUP_COL_TARGET"), render: (row) => row.target ?? "—" },
              { key: "result", title: L("ADM_PPL_COL_RESULT"), render: () => L("ADM_PPL_ATTEMPT_OK") },
            ]}
            // RC1 · K4 (AF6): dos líneas; la segunda en una sola línea con «…»: «Hoy 06:18 · Soporte QA › Jorge
            // Paredes» (quién › a quién; el rol queda en la tabla de escritorio).
            mobile={(row) => ({
              title: what(row),
              line: `${whenIn(L, locale, row.occurredAt, zone)} · ${
                row.target ? `${whoName(row)} › ${row.target}` : whoName(row)
              }`,
              oneLine: true,
            })}
          />
          <ListFooter list={list} />
        </>
      )}
    </>
  );
}
